import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Radio,
  Typography,
} from '@mui/material';

import type { PatientSummary } from '../../types';

/**
 * Fields from PatientSummary that can be patched via UpdatePatientRequest.
 * Maps display label → PatientSummary key → UpdatePatientRequest key.
 */
const DEMOGRAPHIC_FIELDS: {
  label: string;
  summaryKey: keyof PatientSummary;
  patchKey: string;
  format?: (value: string) => string;
}[] = [
  { label: 'First Name', summaryKey: 'first_name', patchKey: 'first_name' },
  { label: 'Last Name', summaryKey: 'last_name', patchKey: 'last_name' },
  {
    label: 'Date of Birth',
    summaryKey: 'date_of_birth',
    patchKey: 'dob',
    format: v => new Date(v).toLocaleDateString(),
  },
  { label: 'Email', summaryKey: 'email', patchKey: 'email' },
  { label: 'Phone', summaryKey: 'mobile_phone', patchKey: 'mobile_phone' },
];

export type DemographicSelections = Record<string, string>;

interface DemographicFieldSelectorProps {
  patients: PatientSummary[];
  defaultPatientId: string;
  selections: DemographicSelections;
  onSelectionChange: (patchKey: string, patientId: string) => void;
  patientColors: Map<string, string>;
  expanded?: boolean;
  onToggle?: () => void;
}

export function DemographicFieldSelector({
  patients,
  defaultPatientId,
  selections,
  onSelectionChange,
  patientColors,
  expanded,
  onToggle,
}: DemographicFieldSelectorProps) {
  // Count how many fields are sourced from non-default patients
  const customizedCount = Object.values(selections).filter(id => id !== defaultPatientId).length;

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      disableGutters
      sx={{ '&:before': { display: 'none' } }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          backgroundColor: 'grey.50',
          borderBottom: 1,
          borderColor: 'divider',
          minHeight: 48,
          '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 },
        }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          Demographics
        </Typography>
        <Chip
          label={customizedCount > 0 ? `${customizedCount} customized` : 'Using defaults'}
          size="small"
          color={customizedCount > 0 ? 'info' : 'success'}
          variant="outlined"
          sx={{ height: 22, fontSize: '0.75rem' }}
        />
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        {DEMOGRAPHIC_FIELDS.map(field => (
          <DemographicFieldRow
            key={field.patchKey}
            field={field}
            patients={patients}
            selectedPatientId={selections[field.patchKey] ?? defaultPatientId}
            onSelect={patientId => onSelectionChange(field.patchKey, patientId)}
            patientColors={patientColors}
          />
        ))}
      </AccordionDetails>
    </Accordion>
  );
}

interface DemographicFieldRowProps {
  field: (typeof DEMOGRAPHIC_FIELDS)[number];
  patients: PatientSummary[];
  selectedPatientId: string;
  onSelect: (patientId: string) => void;
  patientColors: Map<string, string>;
}

interface GroupedValue {
  displayValue: string | null;
  patients: { id: string; name: string; color: string }[];
}

function DemographicFieldRow({
  field,
  patients,
  selectedPatientId,
  onSelect,
  patientColors,
}: DemographicFieldRowProps) {
  // Group patients by their display value so identical values share a single row
  const groups: GroupedValue[] = [];
  const groupIndex = new Map<string, number>();

  for (const patient of patients) {
    const rawValue = patient[field.summaryKey];
    const displayValue =
      rawValue != null && rawValue !== ''
        ? field.format
          ? field.format(String(rawValue))
          : String(rawValue)
        : null;
    const groupKey = displayValue ?? '__empty__';
    const color = patientColors.get(patient.patient_id) ?? '#888';
    const patientName =
      [patient.first_name, patient.last_name].filter(Boolean).join(' ') ||
      patient.patient_id.substring(0, 8);

    const existing = groupIndex.get(groupKey);
    if (existing != null) {
      groups[existing].patients.push({ id: patient.patient_id, name: patientName, color });
    } else {
      groupIndex.set(groupKey, groups.length);
      groups.push({
        displayValue,
        patients: [{ id: patient.patient_id, name: patientName, color }],
      });
    }
  }

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
      }}
    >
      <Typography
        variant="caption"
        fontWeight={600}
        sx={{ px: 1.5, pt: 1, pb: 0.5, display: 'block', color: 'text.secondary' }}
      >
        {field.label}
      </Typography>
      <Box sx={{ px: 0.5, pb: 0.5 }}>
        {groups.map(group => {
          // The row is selected if any patient in this group is the selected source
          const isSelected = group.patients.some(p => p.id === selectedPatientId);
          // Use first patient's ID as the selection target
          const firstPatient = group.patients[0];

          return (
            <Box
              key={group.displayValue ?? '__empty__'}
              onClick={() => onSelect(firstPatient.id)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                py: 0.5,
                px: 1,
                borderRadius: 1,
                cursor: 'pointer',
                '&:hover': { backgroundColor: 'grey.50' },
              }}
            >
              <Radio
                size="small"
                checked={isSelected}
                onChange={() => onSelect(firstPatient.id)}
                sx={{ p: 0.5, color: firstPatient.color }}
                inputProps={{
                  'aria-label': `Use ${field.label}: ${group.displayValue ?? 'empty'}`,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  color: group.displayValue ? 'text.primary' : 'text.disabled',
                }}
                noWrap
              >
                {group.displayValue ?? '—'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                {group.patients.map(p => (
                  <Chip
                    key={p.id}
                    label={p.name}
                    size="small"
                    variant="outlined"
                    sx={{
                      height: 20,
                      fontSize: '0.65rem',
                      borderColor: p.color,
                      color: p.color,
                    }}
                  />
                ))}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

export { DEMOGRAPHIC_FIELDS };
